<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Create New Order</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a comprehensive order with items, pricing, and delivery details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-cart-plus</v-icon>
                      New Order Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Orders
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <!-- Order Information Section -->
              <div class="form-section mb-2">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-receipt</v-icon>
                  Order Information
                </h3>
                
                <v-row>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      ref="seller"
                      outlined
                      v-model="form.seller_id"
                      :items="sellers"
                      item-text="text"
                      item-value="value"
                      label="Seller *"
                      clearable
                      :error-messages="errors.seller_id"
                      @change="onSellerChange"
                      class="professional-field"
                      hide-details="auto"
                      required
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-account-tie</v-icon>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar size="32" class="mr-2">
                          <v-img
                            :src="item.image_path || '/images/default-user.png'"
                            :alt="item.text"
                          />
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">{{ item.text }}</v-list-item-title>
                          <v-list-item-subtitle v-if="item.phone" class="text-caption">
                            <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ item.phone }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      ref="customer"
                      outlined
                      v-model="form.customer_id"
                      :items="customers"
                      item-text="text"
                      item-value="value"
                      label="Customer *"
                      clearable
                      :error-messages="errors.customer_id"
                      @change="onCustomerChange"
                      class="professional-field"
                      hide-details="auto"
                      required
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-account-heart</v-icon>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar size="32" class="mr-2">
                          <v-img
                            :src="item.image_path || '/images/default-user.png'"
                            :alt="item.text"
                          />
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">{{ item.text }}</v-list-item-title>
                          <v-list-item-subtitle v-if="item.phone" class="text-caption">
                            <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ item.phone }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      value="Auto-generated after creation"
                      outlined
                      label="Order Number"
                      readonly
                      disabled
                      hint="Format: ORD/DDMMYYYY/1234"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-identifier</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      outlined
                      v-model="form.payment_type"
                      :items="paymentTypes"
                      item-text="text"
                      item-value="value"
                      label="Payment Type *"
                      :error-messages="errors.payment_type"
                      class="professional-field"
                      hide-details="auto"
                      required
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-credit-card</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </div>

              <!-- Customer Address Section -->
              <div class="form-section mb-2" v-if="form.customer_id">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                  Customer Delivery Address
                </h3>
                
                <v-row>
                  <v-col cols="12" md="8">
                    <v-textarea
                      v-model="form.customer_address"
                      outlined
                      label="Complete Address *"
                      :error-messages="errors.customer_address"
                      rows="2"
                      auto-grow
                      class="professional-field"
                      hide-details="auto"
                      required
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-home</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.customer_phone"
                      outlined
                      label="Contact Phone"
                      :error-messages="errors.customer_phone"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-phone</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.customer_city"
                      outlined
                      label="City"
                      :error-messages="errors.customer_city"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-city</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.customer_state"
                      outlined
                      label="State"
                      :error-messages="errors.customer_state"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-map</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.customer_pincode"
                      outlined
                      label="Pincode"
                      type="number"
                      :error-messages="errors.customer_pincode"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-mailbox</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Seller Store Information Section -->
              <div class="form-section mb-2" v-if="form.seller_id">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-store</v-icon>
                  Seller Store Information
                </h3>
                
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.seller_store_name"
                      outlined
                      label="Store Name"
                      :error-messages="errors.seller_store_name"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-store</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.seller_gst_number"
                      outlined
                      label="GST Number"
                      :error-messages="errors.seller_gst_number"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-file-document</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-textarea
                      v-model="form.seller_store_address"
                      outlined
                      label="Store Address"
                      :error-messages="errors.seller_store_address"
                      rows="2"
                      auto-grow
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-home</v-icon>
                      </template>
                    </v-textarea>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.seller_store_phone"
                      outlined
                      label="Store Phone"
                      :error-messages="errors.seller_store_phone"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-phone</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.seller_store_city"
                      outlined
                      label="Store City"
                      :error-messages="errors.seller_store_city"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-city</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.seller_store_state"
                      outlined
                      label="Store State"
                      :error-messages="errors.seller_store_state"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-map</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.seller_store_pincode"
                      outlined
                      label="Store Pincode"
                      type="number"
                      :error-messages="errors.seller_store_pincode"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-mailbox</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Order Items Section -->
              <div class="form-section mb-2">
                <div class="d-flex justify-space-between align-center mb-1">
                  <h3 class="section-title mb-0">
                    <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                    Order Items ({{ orderItems.length }})
                  </h3>
                  <v-btn
                    color="success"
                    small
                    @click="addOrderItem"
                    :disabled="!products.length"
                    class="action-button"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Add Item
                  </v-btn>
                </div>
                <div v-if="orderItems.length === 0" class="text-center py-4 text-grey">
                  <v-icon large color="grey lighten-2">mdi-package-variant-closed</v-icon>
                  <p class="mt-2">No items added yet. Click "Add Item" to get started.</p>
                </div>
                
                <v-row v-for="(item, index) in orderItems" :key="index" class="mb-2">
                  <v-col cols="12">
                    <v-card outlined class="order-item-card">
                      <v-card-title class="py-2 px-3">
                        <span class="text-subtitle-2">Item #{{ index + 1 }}</span>
                        <v-spacer></v-spacer>
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="removeOrderItem(index)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-card-title>
                      <v-card-text class="pt-2">
                        <v-row>
                          <v-col cols="12" sm="6">
                            <v-autocomplete
                              v-model="item.selected_variant_id"
                              :items="products"
                              item-text="name"
                              item-value="id"
                              label="Product *"
                              outlined
                              dense
                              :error-messages="getItemError(index, 'product_id')"
                              @change="onProductChange(index)"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-cube-outline</v-icon>
                              </template>
                              <template v-slot:item="{ item: product }">
                                <div class="d-flex align-center">
                                  <v-avatar size="24" class="mr-2">
                                    <v-img
                                      :src="product.image_path || '/images/default-product.png'"
                                      :alt="product.name"
                                    />
                                  </v-avatar>
                                  <div class="d-flex flex-column">
                                    <div class="font-weight-medium">{{ product.name }}</div>
                                    <div class="text-caption text-grey">
                                      ₹{{ parseFloat(product.price || 0).toFixed(2) }}
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" sm="2">
                            <v-text-field
                              v-model.number="item.quantity"
                              label="Quantity *"
                              outlined
                              dense
                              type="number"
                              min="1"
                              :error-messages="getItemError(index, 'quantity')"
                              @input="calculateItemTotal(index)"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-numeric</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="2">
                            <v-text-field
                              v-model.number="item.unit_price"
                              label="Unit Price *"
                              outlined
                              dense
                              type="number"
                              step="0.01"
                              min="0"
                              :error-messages="getItemError(index, 'unit_price')"
                              @input="calculateItemTotal(index)"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-currency-inr</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="2">
                            <v-text-field
                              :value="item.total_price"
                              label="Total"
                              outlined
                              dense
                              readonly
                              prefix="₹"
                              class="professional-field total-field"
                              hide-details="auto"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        
                        <!-- Additional Item Details Row -->
                        <v-row>
                          <v-col cols="12" sm="3">
                            <v-text-field
                              v-model.number="item.discount_amount"
                              label="Discount Amount"
                              outlined
                              dense
                              type="number"
                              step="0.01"
                              min="0"
                              @input="calculateItemTotal(index)"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-tag-minus</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="3">
                            <v-text-field
                              v-model.number="item.tax_percentage"
                              label="Tax (%)"
                              outlined
                              dense
                              type="number"
                              min="0"
                              max="100"
                              @input="calculateItemTotal(index)"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-percent</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="3">
                            <v-text-field
                              :value="item.tax_amount"
                              label="Tax Amount"
                              outlined
                              dense
                              readonly
                              prefix="₹"
                              class="professional-field"
                              hide-details="auto"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="3">
                            <v-text-field
                              :value="item.final_amount"
                              label="Final Amount"
                              outlined
                              dense
                              readonly
                              prefix="₹"
                              class="professional-field final-amount-field"
                              hide-details="auto"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        
                        <!-- Package Details Row (Optional) -->
                        <v-expansion-panels class="mt-2">
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
                                <span class="text-subtitle-2">Package Details (Optional)</span>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <v-row>
                                <v-col cols="12" sm="3">
                                  <v-text-field
                                    v-model.number="item.package_weight"
                                    label="Weight (kg)"
                                    outlined
                                    dense
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    class="professional-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey">mdi-weight-kilogram</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                  <v-text-field
                                    v-model.number="item.package_length"
                                    label="Length (cm)"
                                    outlined
                                    dense
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    class="professional-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey">mdi-ruler</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                  <v-text-field
                                    v-model.number="item.package_breadth"
                                    label="Width (cm)"
                                    outlined
                                    dense
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    class="professional-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey">mdi-ruler</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                  <v-text-field
                                    v-model.number="item.package_height"
                                    label="Height (cm)"
                                    outlined
                                    dense
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    class="professional-field"
                                    hide-details="auto"
                                  >
                                    <template v-slot:prepend-inner>
                                      <v-icon color="grey">mdi-ruler</v-icon>
                                    </template>
                                  </v-text-field>
                                </v-col>
                              </v-row>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Order Summary Section -->
              <div class="form-section mb-2">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-calculator</v-icon>
                  Order Summary
                </h3>
                
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :value="calculatedSubtotal"
                      outlined
                      label="Subtotal (Auto-calculated)"
                      readonly
                      prefix="₹"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-currency-inr</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="form.delivery_charges"
                      outlined
                      label="Delivery Charges"
                      :error-messages="errors.delivery_charges"
                      type="number"
                      step="0.01"
                      min="0"
                      @input="calculateAmounts"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-truck-delivery</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :value="calculatedFinalAmount"
                      outlined
                      label="Final Amount"
                      readonly
                      prefix="₹"
                      class="professional-field final-amount-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-currency-inr</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Order Status Section -->
              <div class="form-section mb-2">
                <h3 class="section-title mb-1">
                  <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
                  Order Status
                </h3>
                
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-autocomplete
                      outlined
                      v-model="form.order_status"
                      :items="orderStatuses"
                      item-text="text"
                      item-value="value"
                      label="Order Status *"
                      :error-messages="errors.order_status"
                      class="professional-field"
                      hide-details="auto"
                      required
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-package-variant</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      value="Auto-generated after creation"
                      outlined
                      label="Invoice Number"
                      readonly
                      disabled
                      hint="Format: HT/ORD2025/0001"
                      class="professional-field"
                      hide-details="auto"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey">mdi-receipt</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Order Settings -->
                <v-row class="mt-2">
                  <v-col cols="12" md="6">
                    <v-card outlined class="status-card">
                      <v-card-text class="pa-2">
                        <div class="d-flex align-center">
                          <v-icon color="grey" class="mr-3">mdi-toggle-switch</v-icon>
                          <div class="flex-grow-1">
                            <div class="font-weight-medium mb-1">Order Status</div>
                            <div class="text-caption grey--text">Set the order active status</div>
                          </div>
                          <v-switch
                            v-model="form.is_active"
                            color="success"
                            inset
                            hide-details
                            class="ml-3"
                          ></v-switch>
                        </div>
                        <div class="mt-2">
                          <v-chip 
                            small 
                            :color="form.is_active ? 'success' : 'warning'"
                            :text-color="form.is_active ? 'white' : 'black'"
                          >
                            {{ form.is_active ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="orderItems.length === 0 ? 'error' : 'success'"
                      text-color="white"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ orderItems.length === 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ orderItems.length === 0 ? 'Please add order items' : 'Order ready to save' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{ orderItems.length }} item{{ orderItems.length === 1 ? '' : 's' }} added
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isLoading"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="isLoading || orderItems.length === 0"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Order
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "OrderCreatePage",
  data() {
    return {
      form: {
        seller_id: "",
        customer_id: "",
        payment_type: 2,
        order_status: 0, // 0=Pending, 1=Processing, etc.
        // Customer Address Details
        customer_address: "",
        customer_city: "",
        customer_state: "",
        customer_pincode: "",
        customer_phone: "",
        // Seller Store Details
        seller_store_name: "",
        seller_store_address: "",
        seller_store_city: "",
        seller_store_state: "",
        seller_store_pincode: "",
        seller_store_phone: "",
        seller_gst_number: "",
        delivery_charges: 0,
        is_active: true,
      },
      orderItems: [],
      sellers: [],
      customers: [],
      products: [],
      paymentTypes: [
        { text: "COD (Cash on Delivery)", value: 1 },
        { text: "Prepaid/UPI", value: 2 },
        { text: "Wallet", value: 3 },
      ],
      orderStatuses: [
        { text: "Pending", value: 0 },
        { text: "Processing", value: 1 },
        { text: "Packed", value: 2 },
        { text: "Out For Pickup", value: 3 },
        { text: "In Transit", value: 4 },
        { text: "Out For Delivery", value: 5 },
        { text: "Delivered", value: 6 },
        { text: "Exception", value: 7 },
        { text: "RTO", value: 8 },
        { text: "RTO In Transit", value: 9 },
        { text: "RTO Delivered", value: 10 },
      ],
      isLoading: false,
      errors: {},
    };
  },
  computed: {
    calculatedSubtotal() {
      return this.orderItems.reduce((total, item) => {
        return total + (parseFloat(item.final_amount) || 0);
      }, 0).toFixed(2);
    },
    calculatedFinalAmount() {
      const subtotal = parseFloat(this.calculatedSubtotal) || 0;
      const deliveryCharges = parseFloat(this.form.delivery_charges) || 0;
      return (subtotal + deliveryCharges).toFixed(2);
    },
  },
  mounted() {
    this.getMasters();
    this.getProducts();
  },
  methods: {
    async getMasters() {
      try {
        const response = await axios.get("masters?context=orders");
        const masters = response.data;
        
        if (masters.users) {
          const users = masters.users;
          
          // Filter users by position - sellers and patients
          const sellerUsers = users.filter(user => 
            user.position && user.position.name && 
            (user.position.name.toLowerCase().includes('seller') || 
             user.position.name.toLowerCase().includes('sales') ||
             user.position.name.toLowerCase().includes('agent'))
          );
          
          const customerUsers = users.filter(user => 
            user.position && user.position.name && 
            (user.position.name.toLowerCase().includes('patient') ||
             user.position.name.toLowerCase().includes('customer') ||
             user.position.name.toLowerCase().includes('user'))
          );
          
          // Populate sellers dropdown with users having seller positions
          this.sellers = sellerUsers.map(user => ({
            id: user.id,
            text: this.getFullName(user),
            value: user.id,
            phone: user.phone,
            email: user.email,
            image_path: user.image_path,
            address: user.address,
            city: user.city,
            state: user.state,
            pincode: user.pincode,
            store_name: user.store_name,
            store_address: user.address,
            store_city: user.city,
            store_state: user.state,
            store_pincode: user.pincode,
            gst_no: user.gst_no,
          }));
          
          // Populate customers dropdown with users having customer/patient positions
          this.customers = customerUsers.map(user => ({
            id: user.id,
            text: this.getFullName(user),
            value: user.id,
            phone: user.phone,
            email: user.email,
            image_path: user.image_path,
            address: user.address,
            city: user.city,
            state: user.state,
            pincode: user.pincode,
          }));
          
          // If no users found with specific positions, show all users as fallback
          if (this.sellers.length === 0) {
            this.sellers = users.map(user => ({
              id: user.id,
              text: this.getFullName(user),
              value: user.id,
              phone: user.phone,
              email: user.email,
              image_path: user.image_path,
              address: user.address || '',
              city: user.city || '',
              state: user.state || '',
              pincode: user.pincode || '',
              store_name: user.store_name || '',
              gst_no: user.gst_no || '',
            }));
          }
          
          if (this.customers.length === 0) {
            this.customers = users.map(user => ({
              id: user.id,
              text: this.getFullName(user),
              value: user.id,
              phone: user.phone,
              email: user.email,
              image_path: user.image_path,
              address: user.address || '',
              city: user.city || '',
              state: user.state || '',
              pincode: user.pincode || '',
            }));
          }
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getProducts() {
      try {
        const response = await axios.get("/product_variants/seller_product_variants");
        console.log("Products API response:", response.data);
        
        if (response.data && response.data.success && response.data.data) {
          this.products = response.data.data.map(variant => ({
            id: variant.id, // This is the product variant ID from the API response
            text: `${variant.name} (${variant.unit?.description || 'Unit'}) - ₹${variant.selling_price}`,
            value: variant.id,
            name: variant.name,
            product_name: variant.product?.name || variant.name,
            brand_name: variant.product?.brand_name || '',
            price: parseFloat(variant.selling_price || variant.maximum_retail_price || 0),
            mrp: parseFloat(variant.maximum_retail_price || 0),
            selling_price: parseFloat(variant.selling_price || 0),
            product_id: variant.product_id,
            seller_product_variant_id: variant.seller_product_variant_id, // This is the actual seller product variant ID we need
            sku_id: variant.sku_id,
            stock: parseInt(variant.stock || 0),
            unit: variant.unit?.description || 'Unit',
            packing_type: variant.packing_type?.description || 'Box'
          }));
          console.log("Product variants loaded:", this.products.length);
          console.log("First product variant:", this.products[0]);
        } else {
          console.warn("Products API returned unexpected format:", response.data);
          this.products = [];
        }
      } catch (error) {
        console.error("Error loading products:", error);
        this.products = [];
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async onCustomerChange() {
      if (!this.form.customer_id) return;
      const customer = this.customers.find(c => c.id === this.form.customer_id);
      if (!customer) return;

      // Pre-fill from cached customer object
      this.form.customer_phone   = customer.phone   || '';
      this.form.customer_address = customer.address || '';
      this.form.customer_city    = customer.city    || '';
      this.form.customer_state   = customer.state   || '';
      this.form.customer_pincode = customer.pincode || '';

      // Fetch full customer profile to get their primary delivery address
      try {
        const res = await axios.get(`/users/${this.encryptIt(String(customer.id))}?context=panel`);
        const u = res.data?.data || res.data;
        if (u) {
          if (!this.form.customer_phone) this.form.customer_phone = u.phone || '';

          const primaryAddr = u.user_addresses?.find(a => a.is_primary) || u.user_addresses?.[0];
          if (primaryAddr) {
            this.form.customer_address = primaryAddr.address  || '';
            this.form.customer_city    = primaryAddr.city     || '';
            this.form.customer_state   = primaryAddr.state    || '';
            this.form.customer_pincode = primaryAddr.pincode  || '';
            if (primaryAddr.receiver_phone) this.form.customer_phone = primaryAddr.receiver_phone;
          } else {
            if (!this.form.customer_address) this.form.customer_address = u.address || '';
            if (!this.form.customer_city)    this.form.customer_city    = u.city    || '';
            if (!this.form.customer_state)   this.form.customer_state   = u.state   || '';
            if (!this.form.customer_pincode) this.form.customer_pincode = u.pincode || '';
          }
        }
      } catch (_) {
        // Non-blocking — cached data already filled above
      }
    },
    async onSellerChange() {
      if (!this.form.seller_id) return;
      const seller = this.sellers.find(s => s.id === this.form.seller_id);
      if (!seller) return;

      // Pre-fill from cached seller object
      this.form.seller_store_name    = seller.store_name    || seller.text || '';
      this.form.seller_store_phone   = seller.phone         || '';
      this.form.seller_gst_number    = seller.gst_no        || '';
      this.form.seller_store_address = seller.store_address || seller.address || '';
      this.form.seller_store_city    = seller.store_city    || seller.city    || '';
      this.form.seller_store_state   = seller.store_state   || seller.state   || '';
      this.form.seller_store_pincode = seller.store_pincode || seller.pincode || '';

      // Fetch full seller profile to get their primary/store address if not already filled
      try {
        const res = await axios.get(`/users/${this.encryptIt(String(seller.id))}?context=panel`);
        const u = res.data?.data || res.data;
        if (u) {
          if (!this.form.seller_store_name)    this.form.seller_store_name    = u.store_name   || '';
          if (!this.form.seller_gst_number)    this.form.seller_gst_number    = u.gst_no        || '';
          if (!this.form.seller_store_phone)   this.form.seller_store_phone   = u.phone         || '';

          // Check primary address from user_addresses
          const primaryAddr = u.user_addresses?.find(a => a.is_primary) || u.user_addresses?.[0];
          if (primaryAddr) {
            if (!this.form.seller_store_address) this.form.seller_store_address = primaryAddr.address   || '';
            if (!this.form.seller_store_city)    this.form.seller_store_city    = primaryAddr.city      || '';
            if (!this.form.seller_store_state)   this.form.seller_store_state   = primaryAddr.state     || '';
            if (!this.form.seller_store_pincode) this.form.seller_store_pincode = primaryAddr.pincode   || '';
          } else {
            // Fallback to user profile fields
            if (!this.form.seller_store_address) this.form.seller_store_address = u.address || '';
            if (!this.form.seller_store_city)    this.form.seller_store_city    = u.city    || '';
            if (!this.form.seller_store_state)   this.form.seller_store_state   = u.state   || '';
            if (!this.form.seller_store_pincode) this.form.seller_store_pincode = u.pincode || '';
          }
        }
      } catch (_) {
        // Non-blocking — cached data already filled above
      }
    },
    addOrderItem() {
      this.orderItems.push({
        selected_variant_id: null,
        product_id: null,
        seller_product_variant_id: null,
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        discount_amount: 0,
        tax_percentage: 0,
        tax_amount: 0,
        commission_amount: 0,
        final_amount: 0,
        package_weight: 0,
        package_length: 0,
        package_breadth: 0,
        package_height: 0,
        is_active: true,
      });
    },
    removeOrderItem(index) {
      this.orderItems.splice(index, 1);
      this.calculateAmounts();
    },
    onProductChange(index) {
      const item = this.orderItems[index];
      const variant = this.products.find(p => p.id === item.selected_variant_id);
      if (variant) {
        item.product_id = variant.product_id;
        item.seller_product_variant_id = variant.seller_product_variant_id;
        item.unit_price = parseFloat(variant.selling_price || 0);
        this.calculateItemTotal(index);
      }
    },
    calculateItemTotal(index) {
      const item = this.orderItems[index];
      const quantity = parseInt(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const discountAmount = parseFloat(item.discount_amount) || 0;
      const taxPercentage = parseFloat(item.tax_percentage) || 0;
      
      // Calculate base amount
      const baseAmount = quantity * unitPrice;
      
      // Apply discount
      const afterDiscount = baseAmount - discountAmount;
      
      // Calculate tax amount
      const taxAmount = (afterDiscount * taxPercentage) / 100;
      
      // Calculate final amount for this item
      const finalAmount = afterDiscount + taxAmount;
      
      // Update item values
      item.total_price = baseAmount.toFixed(2);
      item.tax_amount = taxAmount.toFixed(2);
      item.final_amount = finalAmount.toFixed(2);
      
      this.calculateAmounts();
    },
    calculateAmounts() {
      const subtotal = parseFloat(this.calculatedSubtotal) || 0;
      const deliveryCharges = parseFloat(this.form.delivery_charges) || 0;
      this.form.final_amount = (subtotal + deliveryCharges).toFixed(2);
    },
    getItemError(index, field) {
      return this.errors[`order_items.${index}.${field}`] || [];
    },
    getFullName(user) {
      if (!user) return "Unknown User";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || user.user_name || user.email || "No name";
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};
        
        // Validate order items before sending
        const validOrderItems = this.orderItems.filter(item =>
          item.selected_variant_id && item.product_id && item.seller_product_variant_id && parseFloat(item.unit_price) > 0
        );
        
        if (validOrderItems.length === 0) {
          this.errors = { order_items: ['Please add at least one valid order item with product and unit price'] };
          this.isLoading = false;
          return;
        }

        // Ensure numeric values are properly formatted
        const formData = {
          ...this.form,
          delivery_charges: parseFloat(this.form.delivery_charges) || 0,
          order_items: validOrderItems.map(item => ({
            product_id: parseInt(item.product_id),
            seller_product_variant_id: parseInt(item.seller_product_variant_id),
            quantity: parseInt(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price),
            total_price: parseFloat(item.total_price) || 0,
            discount_amount: parseFloat(item.discount_amount) || 0,
            tax_percentage: parseFloat(item.tax_percentage) || 0,
            tax_amount: parseFloat(item.tax_amount) || 0,
            commission_amount: parseFloat(item.commission_amount) || 0,
            final_amount: parseFloat(item.final_amount) || 0,
            package_weight: parseFloat(item.package_weight) || 0,
            package_length: parseFloat(item.package_length) || 0,
            package_breadth: parseFloat(item.package_breadth) || 0,
            package_height: parseFloat(item.package_height) || 0,
            is_active: item.is_active !== false,
          })),
        };
        
        console.log("Order data being sent:", formData);
        
        const response = await axios.post("/orders", formData);
        this.isLoading = false;
        if (response.data.delivery_warning) {
          alert("Order created successfully.\n\nShipping notice: " + response.data.delivery_warning);
        }
        this.$router.push("/orders").catch(() => {});
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        } else {
          console.error("Error saving order:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Order Item Cards */
.order-item-card {
  border-left: 3px solid var(--v-primary-base);
  transition: all 0.2s ease;
}

.order-item-card:hover {
  border-left-color: var(--v-secondary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Special Field Styling */
.total-field >>> .v-input__control {
  background-color: #f5f5f5;
}

.final-amount-field >>> .v-input__control {
  background-color: #e8f5e8;
  font-weight: bold;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card,
.order-item-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>